import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Search, MapPin, Star, Grid3x3, List as ListIcon, Map as MapIcon, CheckCircle2, Sparkles, Crown, Calendar as CalendarIcon, Users, BedDouble } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import WishlistHeart from "@/components/WishlistHeart";

// Fix Leaflet default marker icon (webpack strips images)
const defaultIcon = L.icon({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;
const boostedIcon = L.divIcon({
  html: `<div style="background:#c2532c;color:white;padding:4px 8px;border-radius:20px;font-weight:600;font-size:11px;box-shadow:0 2px 4px rgba(0,0,0,0.3);white-space:nowrap;">★ Featured</div>`,
  className: "boosted-marker", iconSize: [90, 24], iconAnchor: [45, 24],
});

const PAGE_SIZE = 12;

export default function Directory() {
  const { lang, t } = useLang();
  const IT = lang === "it";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | grid | map
  const [mapForProperty, setMapForProperty] = useState(null); // property → open interactive map dialog
  const [facets, setFacets] = useState({ cities: [], regions: [], types: [] });
  const [filters, setFilters] = useState(() => {
    // Bootstrap filters from URL params so links from the hero search bar,
    // "Prenota" links and shared URLs pre-filter the directory.
    const s = new URLSearchParams(window.location.search);
    return {
      q: s.get("q") || "",
      city: s.get("city") || "",
      region: s.get("region") || "",
      property_type: s.get("property_type") || "",
      verified: s.get("verified") === "true",
      free_cancellation: s.get("free_cancellation") === "true",
      check_in: s.get("check_in") || "",
      check_out: s.get("check_out") || "",
      guests: s.get("guests") || "",
      rooms_needed: s.get("rooms_needed") || s.get("rooms") || "",
    };
  });
  const sentinelRef = useRef(null);

  // SEO meta tags
  useEffect(() => {
    const title = IT ? "Strutture ricettive in Italia · HospitalityHub" : "Italian accommodations · HospitalityHub";
    const desc = IT
      ? "Scopri B&B, hotel, agriturismi e case vacanze in Italia. Prenota direttamente con l'host senza commissioni OTA."
      : "Discover B&Bs, hotels, farm stays and villas in Italy. Book directly with the host without OTA commissions.";
    document.title = title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name='${name}']`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", desc);
    setMeta("robots", "index, follow");
    // Open Graph
    const setOG = (prop, content) => {
      let el = document.querySelector(`meta[property='${prop}']`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setOG("og:title", title); setOG("og:description", desc); setOG("og:type", "website");
  }, [IT]);

  // Fetch facets once
  useEffect(() => { api.get("/directory/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const fetchPage = useCallback(async (pg, replace = false) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: PAGE_SIZE };
      if (filters.q) params.q = filters.q;
      if (filters.city) params.city = filters.city;
      if (filters.region) params.region = filters.region;
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.verified) params.verified = true;
      if (filters.free_cancellation) params.free_cancellation = true;
      if (filters.check_in) params.check_in = filters.check_in;
      if (filters.check_out) params.check_out = filters.check_out;
      if (filters.guests) params.guests = filters.guests;
      if (filters.rooms_needed) params.rooms_needed = filters.rooms_needed;
      const r = await api.get("/directory/properties", { params });
      setTotal(r.data.total); setHasMore(r.data.has_more);
      setItems((prev) => replace ? r.data.items : [...prev, ...r.data.items]);
    } catch (e) { console.error("directory fetch", e); }
    finally { setLoading(false); }
  }, [filters]);

  // Reset on filter change
  useEffect(() => {
    setPage(1);
    fetchPage(1, true);
    // no cleanup
  }, [filters]); // eslint-disable-line

  // Infinite scroll observer
  useEffect(() => {
    if (view !== "grid") return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        setPage((p) => { const np = p + 1; fetchPage(np); return np; });
      }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => { try { io.disconnect(); } catch {} };
  }, [view, hasMore, loading, fetchPage]);

  const mapItems = useMemo(() => items.filter((i) => i.lat && i.lng), [items]);
  const mapCenter = mapItems.length > 0 ? [mapItems[0].lat, mapItems[0].lng] : [41.9, 12.5]; // Italy center

  // Forward booking context to the property page so its reservation form pre-fills.
  const bookingQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.check_in) p.set("check_in", filters.check_in);
    if (filters.check_out) p.set("check_out", filters.check_out);
    if (filters.guests) p.set("guests", filters.guests);
    if (filters.rooms_needed) p.set("rooms", filters.rooms_needed);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [filters.check_in, filters.check_out, filters.guests, filters.rooms_needed]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-4">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">
          {IT ? "Directory strutture · Italia" : "Directory · Italy"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">
          {IT ? "Trova la tua struttura in Italia" : "Find your Italian stay"}
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {IT ? `${total} strutture · Prenotazione diretta con l'host, zero commissioni OTA.` : `${total} properties · Direct with the host, no OTA fees.`}
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-6xl mx-auto px-6" data-testid="directory-filters">
        <div className="border border-border bg-card rounded-lg p-4 space-y-3">
          {/* Row 1 · Location + text search */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Cerca" : "Search"}</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder={IT ? "Nome o città…" : "Name or city…"} className="pl-9" data-testid="dir-search"/>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Città" : "City"}</Label>
              <Select value={filters.city || "all"} onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? "" : v })}>
                <SelectTrigger className="w-40" data-testid="dir-city"><SelectValue placeholder={IT ? "Tutte" : "All"}/></SelectTrigger>
                <SelectContent><SelectItem value="all">{IT ? "Tutte" : "All"}</SelectItem>{facets.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Regione" : "Region"}</Label>
              <Select value={filters.region || "all"} onValueChange={(v) => setFilters({ ...filters, region: v === "all" ? "" : v })}>
                <SelectTrigger className="w-40" data-testid="dir-region"><SelectValue placeholder={IT ? "Tutte" : "All"}/></SelectTrigger>
                <SelectContent><SelectItem value="all">{IT ? "Tutte" : "All"}</SelectItem>{facets.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Tipo" : "Type"}</Label>
              <Select value={filters.property_type || "all"} onValueChange={(v) => setFilters({ ...filters, property_type: v === "all" ? "" : v })}>
                <SelectTrigger className="w-36" data-testid="dir-type"><SelectValue placeholder={IT ? "Tutti" : "All"}/></SelectTrigger>
                <SelectContent><SelectItem value="all">{IT ? "Tutti" : "All"}</SelectItem>{facets.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-md cursor-pointer hover:border-primary/40" data-testid="dir-verified">
              <input type="checkbox" checked={filters.verified} onChange={(e) => setFilters({ ...filters, verified: e.target.checked })}/>
              <CheckCircle2 className="w-3.5 h-3.5 text-sage"/>{IT ? "Solo verificate" : "Verified only"}
            </label>
            <label className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-md cursor-pointer hover:border-primary/40" data-testid="dir-free-cancel">
              <input type="checkbox" checked={filters.free_cancellation} onChange={(e) => setFilters({ ...filters, free_cancellation: e.target.checked })}/>
              <CheckCircle2 className="w-3.5 h-3.5 text-sage"/>{IT ? "Cancellazione gratuita" : "Free cancellation"}
            </label>
            <div className="ml-auto flex border border-border rounded-md overflow-hidden" data-testid="dir-view-toggle">
              <button onClick={() => setView("grid")} className={`px-3 py-2 text-xs flex items-center gap-1 ${view === "grid" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-primary/5"}`} data-testid="view-grid">
                <Grid3x3 className="w-3.5 h-3.5"/>{IT ? "Griglia" : "Grid"}
              </button>
              <button onClick={() => setView("list")} className={`px-3 py-2 text-xs flex items-center gap-1 border-l border-border ${view === "list" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-primary/5"}`} data-testid="view-list">
                <ListIcon className="w-3.5 h-3.5"/>{IT ? "Lista" : "List"}
              </button>
              <button onClick={() => setView("map")} className={`px-3 py-2 text-xs flex items-center gap-1 border-l border-border ${view === "map" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-primary/5"}`} data-testid="view-map">
                <MapIcon className="w-3.5 h-3.5"/>{IT ? "Mappa" : "Map"}
              </button>
            </div>
          </div>

          {/* Row 2 · Booking availability */}
          <div className="flex flex-wrap items-end gap-3 border-t border-border/60 pt-3" data-testid="dir-booking-filters">
            <div className="min-w-[140px]">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><CalendarIcon className="w-3 h-3"/>{IT ? "Check-in" : "Check-in"}</Label>
              <Input
                type="date"
                value={filters.check_in}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  const ci = e.target.value;
                  const co = filters.check_out;
                  setFilters({ ...filters, check_in: ci, check_out: (co && ci && co <= ci) ? "" : co });
                }}
                data-testid="dir-check-in"
              />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><CalendarIcon className="w-3 h-3"/>{IT ? "Check-out" : "Check-out"}</Label>
              <Input
                type="date"
                value={filters.check_out}
                min={filters.check_in || new Date().toISOString().slice(0, 10)}
                onChange={(e) => setFilters({ ...filters, check_out: e.target.value })}
                data-testid="dir-check-out"
              />
            </div>
            <div className="w-28">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3"/>{IT ? "Ospiti" : "Guests"}</Label>
              <Input
                type="number" min={1} max={30}
                value={filters.guests}
                onChange={(e) => setFilters({ ...filters, guests: e.target.value })}
                placeholder={IT ? "1+" : "1+"}
                data-testid="dir-guests"
              />
            </div>
            <div className="w-28">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><BedDouble className="w-3 h-3"/>{IT ? "Camere" : "Rooms"}</Label>
              <Input
                type="number" min={1} max={15}
                value={filters.rooms_needed}
                onChange={(e) => setFilters({ ...filters, rooms_needed: e.target.value })}
                placeholder={IT ? "1+" : "1+"}
                data-testid="dir-rooms"
              />
            </div>
            {(filters.check_in || filters.check_out || filters.guests || filters.rooms_needed) && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, check_in: "", check_out: "", guests: "", rooms_needed: "" })}
                className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-primary px-3 py-2"
                data-testid="dir-clear-booking"
              >
                {IT ? "Azzera date/ospiti" : "Reset dates/guests"}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-6 py-6">
        {view === "grid" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="directory-grid">
              {items.map((p) => <PropertyCard key={p.id} p={p} IT={IT} bookingQuery={bookingQuery}/>)}
            </div>
            {!loading && items.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <MapPin className="w-10 h-10 mx-auto opacity-30 mb-2"/>
                {IT ? "Nessuna struttura trovata con i filtri correnti." : "No properties matching current filters."}
              </div>
            )}
            <div ref={sentinelRef} className="h-8 flex items-center justify-center mt-6" data-testid="directory-sentinel">
              {loading && <span className="text-primary text-xs animate-pulse">{IT ? "Caricamento…" : "Loading…"}</span>}
              {!hasMore && items.length > 0 && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Fine risultati" : "End of results"}</span>}
            </div>
          </>
        )}
        {view === "list" && (
          <>
            <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-4">
              <div className="flex flex-col gap-3 min-w-0" data-testid="directory-list">
                {items.map((p) => <PropertyListRow key={p.id} p={p} IT={IT} bookingQuery={bookingQuery} onOpenMap={setMapForProperty}/>)}
              </div>
              {mapItems.length > 0 && (
                <aside className="hidden lg:block lg:sticky lg:top-4 lg:self-start h-[calc(100vh-2rem)] rounded-lg overflow-hidden border border-border" data-testid="directory-side-map">
                  <MapContainer center={mapCenter} zoom={6} style={{ height: "100%", width: "100%" }} className="hh-vintage-map">
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      maxZoom={19}
                      subdomains={["a", "b", "c"]}
                    />
                    <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} spiderfyOnMaxZoom maxClusterRadius={50}>
                      {mapItems.map((p) => (
                        <Marker key={p.id} position={[p.lat, p.lng]} icon={p.boost_active ? boostedIcon : defaultIcon}>
                          <Popup>
                            <div className="text-xs">
                              <div className="font-semibold">{p.name}</div>
                              <div className="text-muted-foreground">{p.city}, {p.region}</div>
                              {p.from_price && <div className="mt-1">{IT ? "Da" : "From"} €{p.from_price}/notte</div>}
                              <Link to={`/p/${p.slug}${bookingQuery}`} className="text-primary underline mt-1 inline-block">{IT ? "Vedi struttura →" : "View property →"}</Link>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  </MapContainer>
                </aside>
              )}
            </div>
            {!loading && items.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <MapPin className="w-10 h-10 mx-auto opacity-30 mb-2"/>
                {IT ? "Nessuna struttura trovata con i filtri correnti." : "No properties matching current filters."}
              </div>
            )}
            <div ref={sentinelRef} className="h-8 flex items-center justify-center mt-6">
              {loading && <span className="text-primary text-xs animate-pulse">{IT ? "Caricamento…" : "Loading…"}</span>}
              {!hasMore && items.length > 0 && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Fine risultati" : "End of results"}</span>}
            </div>
          </>
        )}
        {view === "map" && (
          <div className="h-[70vh] rounded-lg overflow-hidden border border-border" data-testid="directory-map">
            <MapContainer center={mapCenter} zoom={6} style={{ height: "100%", width: "100%" }} className="hh-vintage-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
                subdomains={["a", "b", "c"]}
              />
              <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} spiderfyOnMaxZoom maxClusterRadius={60}>
                {mapItems.map((p) => (
                  <Marker key={p.id} position={[p.lat, p.lng]} icon={p.boost_active ? boostedIcon : defaultIcon}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-muted-foreground">{p.city}, {p.region}</div>
                        {p.from_price && <div className="mt-1">{IT ? "Da" : "From"} €{p.from_price}/notte</div>}
                        <Link to={`/p/${p.slug}${bookingQuery}`} className="text-primary underline mt-1 inline-block">{IT ? "Vedi struttura →" : "View property →"}</Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        )}
      </section>

      {/* Interactive map dialog for a single property */}
      {mapForProperty && (
        <PropertyMapDialog
          property={mapForProperty}
          bookingQuery={bookingQuery}
          IT={IT}
          onClose={() => setMapForProperty(null)}
        />
      )}
    </div>
  );
}

function PropertyCard({ p, IT, bookingQuery = "" }) {
  const desc = IT ? p.description_it : (p.description_en || p.description_it);
  const freeDays = { free_5d: 5, free_3d: 3, free_1d: 1 }[(p.no_show_penalty || "").toLowerCase()];
  return (
    <Link to={`/p/${p.slug}${bookingQuery}`} className="group block border border-border rounded-lg bg-card overflow-hidden lift" data-testid={`dir-card-${p.slug}`}>
      <div className="relative aspect-[4/3] bg-muted">
        {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
        {p.boost_active && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full flex items-center gap-1 shadow" data-testid="boost-badge">
            <Sparkles className="w-3 h-3"/>{IT ? "In evidenza" : "Featured"}
          </div>
        )}
        {p.verified && (
          <div className="absolute top-2 right-2 bg-sage text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full flex items-center gap-1 shadow">
            <CheckCircle2 className="w-3 h-3"/>{IT ? "Verificata" : "Verified"}
          </div>
        )}
        <div className="absolute bottom-2 right-2">
          <WishlistHeart propertyId={p.id}/>
        </div>
      </div>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{p.city}, {p.region} · {p.property_type}</div>
        <div className="font-display text-xl group-hover:text-primary transition-colors">{p.name}</div>
        {desc && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{desc}</p>}
        {p.from_price && (
          <div className="mt-3 text-xs">
            <span className="text-muted-foreground">{IT ? "Da" : "From"}</span>{" "}
            <span className="font-display text-lg text-foreground">€{p.from_price}</span>
            <span className="text-muted-foreground">/{IT ? "notte" : "night"}</span>
          </div>
        )}
        {freeDays && (
          <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-medium bg-sage/15 text-sage border border-sage/40 rounded-full px-2 py-0.5" data-testid={`dir-free-cancel-badge-${p.slug}`}>
            <CheckCircle2 className="w-3 h-3"/>
            {IT ? `Cancellazione gratuita fino a ${freeDays}${freeDays === 1 ? " giorno" : " giorni"} prima` : `Free cancellation up to ${freeDays} day${freeDays === 1 ? "" : "s"} before`}
          </span>
        )}
      </div>
    </Link>
  );
}

function PropertyListRow({ p, IT, bookingQuery = "", onOpenMap }) {
  const desc = IT ? p.description_it : (p.description_en || p.description_it);
  const amenities = (p.amenities || []).slice(0, 4);
  const freeDays = { free_5d: 5, free_3d: 3, free_1d: 1 }[(p.no_show_penalty || "").toLowerCase()];
  return (
    <Link
      to={`/p/${p.slug}${bookingQuery}`}
      className="group grid grid-cols-[140px_1fr] sm:grid-cols-[220px_1fr_200px] gap-0 border border-border rounded-lg bg-card overflow-hidden hover:border-primary/40 hover:shadow-sm transition-all min-h-40 sm:min-h-44"
      data-testid={`dir-row-${p.slug}`}
    >
      <div className="relative bg-muted h-full overflow-hidden min-h-[160px]">
        {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
        {p.boost_active && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full flex items-center gap-1 shadow" data-testid="boost-badge-list">
            <Sparkles className="w-3 h-3"/>{IT ? "In evidenza" : "Featured"}
          </div>
        )}
        <div className="absolute top-2 right-2">
          <WishlistHeart propertyId={p.id}/>
        </div>
      </div>
      <div className="p-4 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3"/>{p.city}, {p.region}</span>
          <span className="opacity-40">·</span>
          <span>{p.property_type}</span>
          {p.verified && (
            <>
              <span className="opacity-40">·</span>
              <span className="inline-flex items-center gap-0.5 text-sage"><CheckCircle2 className="w-3 h-3"/>{IT ? "Verificata" : "Verified"}</span>
            </>
          )}
          {freeDays && (
            <>
              <span className="opacity-40">·</span>
              <span className="inline-flex items-center gap-0.5 text-sage font-medium" data-testid={`dir-row-free-cancel-${p.slug}`}>
                <CheckCircle2 className="w-3 h-3"/>{IT ? `Cancellazione gratuita ${freeDays}${freeDays === 1 ? "g" : "gg"}` : `Free cancel ${freeDays}d`}
              </span>
            </>
          )}
        </div>
        <div className="font-display text-xl group-hover:text-primary transition-colors truncate">{p.name}</div>
        {desc && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{desc}</p>}
        {amenities.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {amenities.map((a) => (
              <span key={a} className="text-[10px] uppercase tracking-widest text-muted-foreground border border-border rounded-full px-2 py-0.5">{a}</span>
            ))}
            {p.amenities && p.amenities.length > 4 && (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">+{p.amenities.length - 4}</span>
            )}
          </div>
        )}
        {p.matched_room?.matched_rooms?.length > 1 ? (
          <div className="sm:hidden mt-2 text-xs space-y-0.5">
            <div className="text-primary font-semibold">{IT ? `${p.matched_room.matched_rooms.length} camere` : `${p.matched_room.matched_rooms.length} rooms`}</div>
            {p.matched_room.total_price != null && (
              <div>
                <span className="text-sage font-semibold">{IT ? "Totale" : "Total"} €{Math.round(p.matched_room.total_price)}</span>
                <span className="text-muted-foreground"> · {p.matched_room.nights} {IT ? "notti" : "nights"}</span>
              </div>
            )}
          </div>
        ) : p.matched_room ? (
          <div className="sm:hidden mt-2 text-xs">
            <span className="text-muted-foreground">{p.matched_room.room_name}</span>{" "}
            <span className="font-display text-lg text-foreground">€{Math.round(p.matched_room.price_per_night)}</span>
            <span className="text-muted-foreground">/{IT ? "notte" : "night"}</span>
            {p.matched_room.total_price != null && (
              <span className="text-sage ml-2">· {IT ? "Tot" : "Tot"} €{Math.round(p.matched_room.total_price)}</span>
            )}
          </div>
        ) : p.from_price && (
          <div className="sm:hidden mt-2 text-xs">
            <span className="text-muted-foreground">{IT ? "Da" : "From"}</span>{" "}
            <span className="font-display text-lg text-foreground">€{p.from_price}</span>
            <span className="text-muted-foreground">/{IT ? "notte" : "night"}</span>
          </div>
        )}
      </div>
      <div className="hidden sm:flex flex-col items-end justify-between p-4 border-l border-border/60 bg-sand/20 min-w-[180px]">
        {p.matched_room?.matched_rooms?.length > 1 ? (
          <div className="text-right w-full">
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">
              {IT ? `${p.matched_room.matched_rooms.length} camere consigliate` : `${p.matched_room.matched_rooms.length} rooms suggested`}
            </div>
            <div className="space-y-0.5 mb-1">
              {p.matched_room.matched_rooms.slice(0, 3).map((rm) => (
                <div key={rm.room_id} className="text-[10px] text-muted-foreground flex items-baseline justify-end gap-1.5">
                  <span className="truncate max-w-[110px]" title={rm.room_name}>{rm.room_name}</span>
                  <span className="opacity-60">{rm.capacity}p</span>
                  <span className="text-foreground font-semibold">€{Math.round(rm.price_per_night)}</span>
                </div>
              ))}
              {p.matched_room.matched_rooms.length > 3 && (
                <div className="text-[10px] text-muted-foreground">+ {p.matched_room.matched_rooms.length - 3}</div>
              )}
            </div>
            {p.matched_room.total_price != null ? (
              <>
                <div className="text-[10px] uppercase tracking-widest text-sage font-semibold">{IT ? "Totale combinato" : "Combined total"}</div>
                <div className="font-display text-2xl text-foreground leading-none">€{Math.round(p.matched_room.total_price)}</div>
                <div className="text-[10px] text-muted-foreground">{p.matched_room.nights} {IT ? "notti" : "nights"}</div>
              </>
            ) : (
              <div className="font-display text-2xl text-foreground leading-none">€{Math.round(p.matched_room.price_per_night)}<span className="text-[10px] text-muted-foreground font-sans"> /{IT ? "notte" : "night"}</span></div>
            )}
            {p.matched_room.fits_all_guests === false && (
              <div className="text-[10px] text-amber-700 mt-1">{IT ? "Capacità insufficiente" : "Not enough capacity"}</div>
            )}
          </div>
        ) : p.matched_room ? (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate max-w-[160px]" title={p.matched_room.room_name}>
              {p.matched_room.room_name}
              {p.matched_room.capacity ? ` · ${p.matched_room.capacity}p` : ""}
            </div>
            <div className="font-display text-2xl text-foreground leading-none mt-0.5">€{Math.round(p.matched_room.price_per_night)}</div>
            <div className="text-[10px] text-muted-foreground">/{IT ? "notte" : "night"}</div>
            {p.matched_room.total_price != null && (
              <div className="text-[11px] text-sage mt-1 font-semibold">
                {IT ? "Totale" : "Total"} €{Math.round(p.matched_room.total_price)}
                {p.matched_room.nights ? ` · ${p.matched_room.nights} ${IT ? "notti" : "nights"}` : ""}
              </div>
            )}
          </div>
        ) : p.from_price ? (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Da" : "From"}</div>
            <div className="font-display text-2xl text-foreground leading-none">€{p.from_price}</div>
            <div className="text-[10px] text-muted-foreground">/{IT ? "notte" : "night"}</div>
          </div>
        ) : <div/>}
        <span className="text-[11px] uppercase tracking-widest text-primary group-hover:underline whitespace-nowrap mt-2">
          {IT ? "Vedi struttura →" : "View property →"}
        </span>
        {p.lat != null && p.lng != null && onOpenMap && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenMap(p); }}
            className="text-[11px] inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-full border border-sage/40 text-sage hover:bg-sage hover:text-white transition-colors whitespace-nowrap w-fit"
            data-testid={`dir-row-map-${p.slug}`}
            title={IT ? "Apri la mappa interattiva" : "Open interactive map"}
          >
            <MapPin className="w-3 h-3"/>
            {IT ? "Mappa" : "Map"}
          </button>
        )}
      </div>
    </Link>
  );
}

function PropertyMapDialog({ property, bookingQuery = "", IT, onClose }) {
  if (!property || property.lat == null || property.lng == null) return null;
  const pos = [property.lat, property.lng];
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${property.lat},${property.lng}`;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden" data-testid={`property-map-dialog-${property.slug}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{property.name}</DialogTitle>
          <DialogDescription>
            <MapPin className="w-3 h-3 inline mr-1"/>
            {property.address ? `${property.address} · ` : ""}{property.city}, {property.region}
          </DialogDescription>
        </DialogHeader>
        <div className="h-[60vh] rounded-md overflow-hidden border border-border">
          <MapContainer center={pos} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom className="hh-vintage-map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              subdomains={["a", "b", "c"]}
            />
            <Marker position={pos} icon={property.boost_active ? boostedIcon : defaultIcon}>
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold">{property.name}</div>
                  <div className="text-muted-foreground">{property.city}, {property.region}</div>
                  {property.from_price && <div className="mt-1">{IT ? "Da" : "From"} €{property.from_price}/{IT ? "notte" : "night"}</div>}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
        <div className="flex justify-between items-center gap-2 pt-3">
          <a
            href={gmaps}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline hover:no-underline"
            data-testid="property-map-directions"
          >
            {IT ? "Ottieni indicazioni su Google Maps ↗" : "Get directions on Google Maps ↗"}
          </a>
          <Link
            to={`/p/${property.slug}${bookingQuery}`}
            className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
            data-testid="property-map-view-btn"
          >
            {IT ? "Vedi struttura →" : "View property →"}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

